// ============================================
// Path: /frontend/src/pages/PaymentPage.jsx
// Purpose: Payment processing page for listing subscription plans
// CSS OPTIMIZED: Mobile-first responsive design
// ============================================

// ========================================
// IMPORTS
// ========================================
import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth.jsx';
import { useToast } from '../hooks/useToast';
import { listingsAPI } from '../api/listings.api';
import { usersAPI } from '../api/users.api';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from '../components/ui/card';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { Loader2, CheckCircle } from 'lucide-react';

// ========================================
// MAIN COMPONENT
// ========================================
export default function PaymentPage() {
  console.log('🎨 [PaymentPage] Component mounted');

  // ========================================
  // HOOKS & STATE
  // ========================================
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [selectedPlan, setSelectedPlan] = useState(null);
  const [pendingListing, setPendingListing] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // ========================================
  // DATA FETCHING
  // ========================================
 
  useEffect(() => {
    console.log('🔵 [PaymentPage] Loading session data...');

    const listingData = sessionStorage.getItem('pendingListing');
    const planData = sessionStorage.getItem('selectedPlan');
    const reactivatingListingId = sessionStorage.getItem('reactivatingListingId');
    const renewingSubscription = sessionStorage.getItem('renewingSubscription');

    // ✅ NEW: For reactivation, we only need plan data
    if (reactivatingListingId && planData) {
      const parsedPlan = JSON.parse(planData);
      console.log('✅ [PaymentPage] Reactivation flow - plan loaded:', parsedPlan.name);
      
      setPendingListing({ isReactivation: true, listingId: reactivatingListingId });
      setSelectedPlan(parsedPlan);
      return;
    }

    // ⭐ NEW: For renewal, we only need plan data (no listing)
    if (renewingSubscription && planData) {
      const parsedPlan = JSON.parse(planData);
      console.log('✅ [PaymentPage] Renewal flow - plan loaded:', parsedPlan.name);
      
      setPendingListing({ isRenewal: true });
      setSelectedPlan(parsedPlan);
      return;
    }

    // Original flow for new listings
    if (!listingData || !planData) {
      console.log('⚠️ [PaymentPage] Missing session data');
      toast({
        variant: 'destructive',
        title: 'Session Expired',
        description: 'Missing listing or plan data. Please start over.',
      });
      navigate('/listings/create');
      return;
    }

    const parsedListing = JSON.parse(listingData);
    const parsedPlan = JSON.parse(planData);

    console.log('✅ [PaymentPage] Session data loaded:', {
      listing: parsedListing.title,
      plan: parsedPlan.name,
    });

    setPendingListing(parsedListing);
    setSelectedPlan(parsedPlan);
  }, [navigate, toast]);

  // ========================================
  // HELPER FUNCTIONS
  // ========================================
  const saveListingAndSubscription = async () => {
    if (!pendingListing || !user || !selectedPlan) return;

    console.log('🔵 [PaymentPage] Saving listing and subscription...');

    try {
      // 1. Create the listing
      const listingData = {
        ...pendingListing,
        plan: selectedPlan.name.toUpperCase(),
      };

      console.log('🔵 [PaymentPage] Creating listing...', listingData);
      const listingResponse = await listingsAPI.createListing(listingData);
      console.log('✅ [PaymentPage] Listing created:', listingResponse);

      // 2. Update user's subscription tier
      console.log('🔵 [PaymentPage] Updating user subscription...');
      await usersAPI.updateUser(user.id, {
        subscriptionTier: selectedPlan.name.toUpperCase(),
      });
      console.log('✅ [PaymentPage] Subscription updated');
    } catch (error) {
      console.error('❌ [PaymentPage] Save error:', error);
      throw new Error(
        error.response?.data?.message ||
          'Could not save your listing or update subscription. Please contact support.'
      );
    }
  };

  // ========================================
  // EVENT HANDLERS
  // ========================================
  
  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    console.log('👤 [PaymentPage] Payment form submitted');

    const phoneNumber = e.target.phone.value;
    setIsProcessing(true);

    const reactivatingListingId = sessionStorage.getItem('reactivatingListingId');
    const renewingSubscription = sessionStorage.getItem('renewingSubscription');

    try {
      // 1. Initiate M-Pesa STK Push
      console.log('🔵 [PaymentPage] Initiating M-Pesa payment...');
      
      // ✅ FIX: Structure metadata correctly for backend
      const paymentPayload = {
        phoneNumber,
        amount: selectedPlan.price,
        plan: selectedPlan.name.toUpperCase(),

// ✅ FIX: Correctly determine payment type based on session flags
       metadata: reactivatingListingId 
  ? {
      type: 'LISTING_REACTIVATION',
      reactivatingListingId: reactivatingListingId
    }
  : renewingSubscription
  ? {
      type: 'SUBSCRIPTION_UPGRADE',
      newTier: selectedPlan.name.toUpperCase()
    }
  : pendingListing && !pendingListing.isReactivation && !pendingListing.isRenewal
  ? {
      type: 'LISTING_CREATION',
      // ✅ Only include API-ready fields (exclude imagePreviews)
      title: pendingListing.title,
      description: pendingListing.description,
      price: pendingListing.price,
      county: pendingListing.county,
      subCounty: pendingListing.subCounty,
      category: pendingListing.category,
      listingType: pendingListing.type,
      subType: pendingListing.subType,
      make: pendingListing.make,
      model: pendingListing.model,
      tags: pendingListing.tags,
      allowGroupBuy: pendingListing.allowGroupBuy,
      totalShares: pendingListing.totalShares,
      sharePrice: pendingListing.sharePrice,
      fundingDeadline: pendingListing.fundingDeadline,
      imageUrl: [] // Placeholder - will upload after payment
    }
  : {
      type: 'SUBSCRIPTION_UPGRADE',
      newTier: selectedPlan.name.toUpperCase()
    }
        
   };

      console.log('🔵 [PaymentPage] Payment payload:', paymentPayload);
      
      const paymentResponse = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:5000/api'}/payments/initiate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(paymentPayload)
      });

      const paymentData = await paymentResponse.json();
      
      if (!paymentData.success) {
        throw new Error(paymentData.message || 'Failed to initiate payment');
      }

      console.log('✅ [PaymentPage] STK Push sent. Navigating to status page...');
      
      const paymentId = paymentData.paymentId;

      // 2. Store payment details in sessionStorage (backup)
      const paymentDetails = {
        paymentId,
        phoneNumber,
        amount: selectedPlan.price,
        tier: selectedPlan.name.toUpperCase(),
        type: reactivatingListingId 
          ? 'LISTING_REACTIVATION' 
          : renewingSubscription 
          ? 'SUBSCRIPTION_UPGRADE' 
          : 'LISTING_CREATION',
        reactivatingListingId: reactivatingListingId || null
      };

      sessionStorage.setItem('paymentDetails', JSON.stringify(paymentDetails));

      // 3. Navigate to PaymentStatusPage
      navigate('/subscription/payment-status', {
        state: paymentDetails,
        replace: true
      });

    } catch (error) {
      console.error('❌ [PaymentPage] Payment error:', error);
      toast({
        variant: 'destructive',
        title: 'Payment Error',
        description: error.message || 'An unexpected error occurred.'
      });
      setIsProcessing(false);
    }
  };

  // ========================================
  // LOADING STATE
  // ========================================
  if (authLoading || !user || !selectedPlan || !pendingListing) {
    console.log('⏳ [PaymentPage] Loading...');
    return (
      <div className="flex flex-col min-h-screen">
        <Header />
        <main className="flex-grow flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin" />
        </main>
        <Footer />
      </div>
    );
  }

  // ========================================
  // RENDER
  // ========================================
  console.log('🎨 [PaymentPage] Rendering payment form');

  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      {/* CSS FIX 1: Page container - py-12 md:py-20 → py-6 sm:py-8 md:py-12 + px-4 */}
      <main className="flex-grow container py-6 sm:py-8 md:py-12 px-4">
        {/* CSS FIX 2: Grid gap and spacing - gap-12 → gap-4 sm:gap-6 md:gap-12 + px-4 */}
        <div className="grid md:grid-cols-2 gap-4 sm:gap-6 md:gap-12 max-w-4xl mx-auto px-4">
          {/* Order Summary */}
          {/* CSS FIX 19: Section spacing - space-y-6 → space-y-3 sm:space-y-4 md:space-y-6 */}
          <div className="space-y-3 sm:space-y-4 md:space-y-6">
            <Card>
              {/* CSS FIX 3: CardHeader padding */}
              <CardHeader className="p-3 sm:p-4 md:p-6">
                {/* CSS FIX 4: CardTitle sizing */}
                <CardTitle className="text-lg sm:text-xl md:text-2xl font-bold">Order Summary</CardTitle>
              </CardHeader>
              {/* CSS FIX 6: CardContent padding */}
              {/* CSS FIX 7: Content spacing - space-y-4 → space-y-2 sm:space-y-3 md:space-y-4 */}
              <CardContent className="p-3 sm:p-4 md:p-6 space-y-2 sm:space-y-3 md:space-y-4">
                <div className="flex justify-between">
                  {/* CSS FIX 8: Text sizing */}
                  <span className="text-xs sm:text-sm text-muted-foreground">Plan</span>
                  <span className="text-xs sm:text-sm font-semibold">{selectedPlan.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-xs sm:text-sm text-muted-foreground">Listings</span>
                  <span className="text-xs sm:text-sm font-semibold">{selectedPlan.listings}</span>
                </div>
                <div className="flex justify-between border-t pt-3 sm:pt-4 mt-3 sm:mt-4">
                  {/* CSS FIX 8: Total text sizing */}
                  <span className="text-base sm:text-lg md:text-xl font-bold">Total</span>
                  <span className="text-base sm:text-lg md:text-xl font-bold">
                    KES {selectedPlan.price.toLocaleString()}
                  </span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="p-3 sm:p-4 md:p-6">
                <CardTitle className="text-lg sm:text-xl md:text-2xl font-bold">Your Listing</CardTitle>
              </CardHeader>
              <CardContent className="p-3 sm:p-4 md:p-6">
                {/* CSS FIX 9: Listing title sizing */}
                <p className="text-sm sm:text-base md:text-lg font-semibold truncate">
                  {pendingListing?.title}
                </p>
                {/* CSS FIX 10: Listing subtitle sizing */}
                <p className="text-xs sm:text-sm text-muted-foreground capitalize">
                  {pendingListing?.type} / {pendingListing?.category}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Payment Form */}
          <Card>
            <CardHeader className="p-3 sm:p-4 md:p-6">
              <CardTitle className="text-lg sm:text-xl md:text-2xl font-bold">Payment Details</CardTitle>
              {/* CSS FIX 5: CardDescription sizing */}
              <CardDescription className="text-xs sm:text-sm md:text-base">
                Enter your payment information to complete the purchase.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-3 sm:p-4 md:p-6">
              <Tabs defaultValue="mpesa">
                {/* CSS FIX 17: TabsList sizing */}
                <TabsList className="grid w-full grid-cols-2 h-9 sm:h-10">
                  {/* CSS FIX 18: TabsTrigger sizing */}
                  <TabsTrigger value="mpesa" className="text-xs sm:text-sm">M-Pesa</TabsTrigger>
                  <TabsTrigger value="card" disabled className="text-xs sm:text-sm">
                    Card
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="mpesa">
                  {/* CSS FIX 11: Form spacing - space-y-6 pt-4 → space-y-3 sm:space-y-4 md:space-y-6 pt-3 sm:pt-4 */}
                  <form onSubmit={handlePaymentSubmit} className="space-y-3 sm:space-y-4 md:space-y-6 pt-3 sm:pt-4">
                    {/* CSS FIX 12: Field spacing - space-y-2 → space-y-1.5 sm:space-y-2 */}
                    <div className="space-y-1.5 sm:space-y-2">
                      {/* CSS FIX 13: Label sizing */}
                      <Label htmlFor="phone" className="text-xs sm:text-sm">M-Pesa Phone Number</Label>
                      {/* CSS FIX 14: Input sizing */}
                      <Input
                        id="phone"
                        type="tel"
                        required
                        placeholder="254721215XXX"
                        className="h-9 sm:h-10 text-xs sm:text-sm"
                      />
                    </div>
                    {/* CSS FIX 15: Helper text sizing - text-xs → text-[10px] sm:text-xs */}
                    <p className="text-[10px] sm:text-xs text-muted-foreground">
                      You will receive a prompt on your phone to enter your M-Pesa
                      PIN to complete the payment.
                    </p>
                    {/* CSS FIX 16: Submit button sizing */}
                    <Button
                      type="submit"
                      className="w-full font-bold text-xs sm:text-sm h-10 sm:h-11"
                      disabled={isProcessing}
                    >
                      {isProcessing && (
                        <Loader2 className="animate-spin mr-2 h-4 w-4" />
                      )}
                      {isProcessing
                        ? 'Processing...'
                        : `Pay KES ${selectedPlan.price.toLocaleString()}`}
                    </Button>
                  </form>
                </TabsContent>
                <TabsContent value="card">
                  {/* Card form can be added here */}
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
}